import { Drawer, MantineBreakpoint, UnstyledButton } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { ReactElement, useEffect } from "react"
import { useLocation } from "react-router-dom"
import styles from "./DrawerWithTrigger.module.css"
import { eventTrackingItems } from "@utils/eventTracking"
import XIcon from "@assets/svg/x.svg?react"

interface DrawerWithTriggerProps {
	triggerIcon: ReactElement | string
	drawerContent: ReactElement | ((close: () => void) => ReactElement)
	withHeader?: boolean
	title?: string
	visibleFrom?: MantineBreakpoint
	location?: "header" | "account-menu"
	openTriggerLabel?: string
	closeTriggerLabel?: string
	hashId?: string
}

const DrawerWithTrigger = ({
	triggerIcon,
	drawerContent,
	withHeader = true,
	title,
	visibleFrom = "base",
	location = "header",
	openTriggerLabel,
	closeTriggerLabel,
	hashId
}: DrawerWithTriggerProps) => {
	const [opened, { open, close }] = useDisclosure(false)

	const routeLocation = useLocation()
	useEffect(() => {
		if (opened) {
			close()
		}
	}, [routeLocation.pathname])

	const handleWidgetToggle = (action: "open" | "close") => {
		if (action === "open") {
			open()
		} else {
			if (hashId && window.location.hash === `#${hashId}`) {
				window.history.replaceState(
					null,
					"",
					`${window.location.pathname}${window.location.search}`
				)
			}
			close()
		}
		const actionItem = action === "open" ? "Widget open" : "Widget close"
		eventTrackingItems({
			event_name: "navigation_click",
			navLinkText: title || "",
			interactionType: actionItem,
			category: "Header Menu click",
			link_url: "",
			event211: 1
		})
	}

	useEffect(() => {
		if (!hashId) return

		if (routeLocation.hash === `#${hashId}`) {
			open()
		} else {
			close()
		}
	}, [hashId, routeLocation.hash, open, close])

	return (
		<>
			<UnstyledButton
				aria-label={openTriggerLabel}
				visibleFrom={visibleFrom}
				display="flex"
				onClick={() => handleWidgetToggle("open")}
				variant={location === "header" ? "icon-hover" : "hover"}
			>
				{triggerIcon}
			</UnstyledButton>
			<Drawer
				visibleFrom={visibleFrom}
				size="sm"
				withCloseButton={withHeader}
				closeButtonProps={{ "aria-label": closeTriggerLabel }}
				opened={opened}
				onClose={() => handleWidgetToggle("close")}
				title={title}
				position="right"
				overlayProps={{
					backgroundOpacity: 0,
					blur: 0,
					bg: "transparent"
				}}
				classNames={styles}
			>
				{!withHeader && (
					<UnstyledButton
						aria-label={closeTriggerLabel}
						display="flex"
						pos="absolute"
						top={24}
						right={24}
						onClick={() => handleWidgetToggle("close")}
					>
						<XIcon />
					</UnstyledButton>
				)}
				{typeof drawerContent === "function"
					? drawerContent(() => handleWidgetToggle("close"))
					: drawerContent}
			</Drawer>
		</>
	)
}

export default DrawerWithTrigger
